import React, { useState, useRef } from "react";
import "./contentpage.css";

import { Link } from "react-router-dom";

import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
function IslamicTradePage4() {
  const contentRef = useRef(null);
  const titleText = "Right to Annul a trade (Khiyār)";
  const links = {
    prev: "/islamic-trade-page-3",
    index: "/index",
    next: "/islamic-trade-page-5",
  };
  const questions = [
    {
      text: "1. Which of the following best describes Khiyār al-Majlis?",
      options: [
        {
          value: "A",
          label:
            "A. The right to annul a trade if a defect is discovered after purchase",
        },
        {
          value: "B",
          label:
            "B. The right to annul a trade as long as both parties have not separated from the place of transaction",
        },
        {
          value: "C",
          label: "C. The right to annul a trade due to price misrepresentation",
        },
        {
          value: "D",
          label:
            "D. The right to annul a trade if a condition is stipulated in the contract",
        },
      ],
      correct: "B",
    },
    {
      text: "2. According to Khiyār al-‘Ayb, when can a buyer annul a trade due to a defect?",
      options: [
        {
          value: "A",
          label: "A. Only if the defect is minor and does not affect usability",
        },
        { value: "B", label: "B. Only if the defect was caused by the buyer" },
        {
          value: "C",
          label:
            "C. If it is proven that the defect existed before the buyer took possession of the good",
        },
        { value: "D", label: "D. Regardless of when the defect occurred" },
      ],
      correct: "C",
    },
    {
      text: "3. What is the key principle behind Khiyār ash-Shart (option by condition)?",
      options: [
        {
          value: "A",
          label: "A. The trade is automatically annulled after 30 days",
        },
        {
          value: "B",
          label: "B. The buyer must return the product if it is defective",
        },
        {
          value: "C",
          label:
            "C. A pre-agreed clause gives the right to annul the trade within a specific period",
        },
        { value: "D", label: "D. The seller can revoke the sale at any time" },
      ],
      correct: "C",
    },
    {
      text: "4. If a seller lies about the purchase price of a product and sells it based on that lie, which type of Khiyār applies?",
      options: [
        { value: "A", label: "A. Khiyār al-Ghabn" },
        { value: "B", label: "B. Khiyār at-Tadlīs" },
        { value: "C", label: "C. Khiyār takhbīr bi thaman" },
        { value: "D", label: "D.  Khiyār al-Majlis" },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={3}
        timerMinutes={60}
        currentLevel={1}
      />
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 4</b> of your journey through Islamic commercial
        jurisprudence. Today, we explore an essential concept that strengthens
        fairness and accountability in trade: Khiyār, which is the right of
        option in sales contracts.
        <br /> The concept of Khiyār in Islamic jurisprudence refers to the
        option or right to annul a trade or sale contract under specific
        conditions. This clause is established to ensure justice and fairness in
        trade, protecting the rights of both buyer and seller.
        <br /> There are various types of Khiyar, such as Khiyar al-Shart
        (option by condition), Khiyar al-‘Ayb (option due to defect), and Khiyar
        al-Majlis (option during the sitting) which we will understand in
        detail. <br />
        <br />
        The right to annual a trade is established from evidences from the
        Quran: <br />
        "O believers! When you contract a loan for a fixed period of time,
        commit it to writing. Let the scribe maintain justice between the
        parties. The scribe should not refuse to write as Allah has taught them
        to write....."
        <a href="https://quran.com/2/282" target="blank">
          (Surah Al Baqarah 2:282)
        </a>
        <br />
        This verse emphasizes documenting transactions and ensuring clarity,
        which aligns with the principles of fairness and mutual consent in
        trade, supporting the concept of Khiyar. <br />
        "O believers! Do not devour one another’s wealth illegally, but rather
        trade by mutual consent..."
        <a href="https://quran.com/4/29" target="blank">
          (Surah An-Nisa 4:29)
        </a>
        <br />
        This verse highlights the importance of mutual consent, and Khiyar
        allows a party to withdraw from a contract if their consent was given
        under duress or they later discover unjust terms. <br />
        <br />
        Evidences from Authentic Hadith: Hadith on Khiyar al-Majlis (Right to
        annul during the sitting): The Prophet Muhammad (peace be upon him)
        said: <br />
        "The seller and the buyer have the option of canceling or confirming the
        deal unless they separate, or one of them says to the other, 'Choose
        (i.e. decide to cancel or confirm the bargain now)."
        <a href="https://sunnah.com/bukhari:2109" target="blank">
          (Sahih Al-Bukhari 2109)
        </a>{" "}
        <br />
        This hadith establishes the right to annul a sale before the parties
        separate from the place where the transaction occurred. <br />
        Hadith on Khiyar al-‘Ayb (Right to annul Due to Defect): The Prophet
        (peace be upon him) said:
        <br />
        "The Muslim is the brother of another Muslim, and it is not permissible
        for a Muslim to sell his brother goods in which there is a defect,
        without pointing that out to him."{" "}
        <a href="https://sunnah.com/ibnmajah:2246" target="blank">
          (Sunan Ibn Majah 2246)
        </a>
        <br />
        This hadith mandates disclosure of defects and allows for annulment if
        the defect is not disclosed. <br />
        <br /> Khiyar provides a mechanism for justice and mutual satisfaction
        in trade by allowing the parties involved to annul the contract under
        certain conditions, ensuring transparency and fairness. It is deeply
        rooted in Islamic teachings from the Quran and Sunnah, safeguarding the
        rights of individuals while promoting ethical business practices.
      </p>
      <h2></h2>
      <table className="trade-table">
        <tr>
          <th>
            {" "}
            Types of Annulments <em>(Aqsām al-Khiyār)</em>
          </th>
        </tr>
        <tr>
          <td>Right to annul a trade during the sitting (Khiyār al-Majlis)</td>
        </tr>
        <tr>
          <td>
            {" "}
            Right to annual a trade when stipulated as a condition in the
            contract (Khiyār ash-Shart)
          </td>
        </tr>
        <tr>
          <td>
            {" "}
            Right to annual a trade in case of a fraud (Khiyār al-Ghabn):
          </td>
        </tr>
        <tr>
          <td>
            Right to annual a trade in case of a misrepresentation (Khiyār
            at-Tadlīs)
          </td>
        </tr>
        <tr>
          <td>Right to annul a trade due to a defect (Khiyār al-‘Ayb):</td>
        </tr>
        <tr>
          <td>
            {" "}
            Right to annul a trade due to incorrect price disclosure (Khiyār
            takhbīr bi thaman)
          </td>
        </tr>
        <tr>
          <td>
            {" "}
            Right to annul a trade due to an irreconcilable dispute (Khiyār li
            Ikhtilāf al mutabāyi'ain)
          </td>
        </tr>
        <tr>
          <td>
            {" "}
            Right to annul due to difference in attributes described (Khiyār lil
            khulf fee as-sifah)
          </td>
        </tr>
        <tr>
          <td>
            {" "}
            Right to annul a trade due to deterioration in the product (Khiyār
            at-taghayyur)
          </td>
        </tr>
      </table>
      <p className="content">
        Let us understand each type in detail.
        <br />
        <br />
        1){" "}
        <b>
          Right to annul a trade during the sitting <em>(Khiyār al-Majlis)</em>:
        </b>
        <br />
        <br />
        In Islamic commercial law, fairness and mutual consent are essential
        pillars of every transaction. One of the mechanisms established to
        uphold this fairness is known as Khiyār al-Majlis, which is the right of
        annulment during the sitting. This principle grants both the buyer and
        the seller the right to revoke or cancel the contract as long as they
        remain within the same meeting or gathering where the sale took place.
        The rationale behind this rule is to ensure that both parties have the
        opportunity to reflect on their decision and confirm that the
        transaction was made without haste, coercion, or misunderstanding.
        <br />
        For example, if a buyer purchases a pair of shoes from a store, both he
        and the seller retain the right to annul the transaction as long as they
        have not yet parted ways i.e the buyer has not exited the store or the
        meeting has not ended. Once they physically separate, the sale becomes
        binding and cannot be revoked on the basis of Khiyār al-Majlis. This
        ruling is derived from authentic narrations of the Prophet Muhammad
        (peace be upon him), who said: <br />
        “The buyer and the seller have the option to cancel or confirm the
        bargain before they separate from each other or if the sale is
        optional.”{" "}
        <a href="https://sunnah.com/bukhari:2107" target="blank">
          (Sahih al-Bukhari 2107)
        </a>
        .<br />
        The wisdom behind Khiyār al-Majlis lies in preserving mutual
        satisfaction and preventing disputes that could arise from impulsive
        decisions or second thoughts immediately after a sale. It reflects
        Islam’s concern for justice, transparency, and ethical dealing in
        financial transactions. Unlike modern commercial systems where a sale
        becomes binding the moment payment is made or a contract is signed,
        Islamic law allows this short window for reconsideration, emphasizing
        that trade is not just a mechanical exchange of goods for money but a
        moral and social interaction that must be free of regret and deception.
        Through this, Islam nurtures a culture of honesty, compassion, and
        mutual respect between traders, qualities that elevate commerce from
        mere profit-seeking to an act of moral responsibility.
      </p>

      <p className="content">
        2){" "}
        <b>
          Right to annual a trade when stipulated as a condition in the contract{" "}
          <em>(Khiyār ash-Shart)</em>:
        </b>{" "}
        <br />
        <br />
        Another form of annulment recognized in Islamic commercial law is Khiyār
        ash-Shart, which is the right to cancel a trade when a specific
        condition has been stipulated in the contract. This right reflects
        Islam’s flexibility and fairness in accommodating the genuine needs of
        both contracting parties. Under Khiyār ash-Shart, if the buyer and the
        seller mutually agree to include a clause that allows either or both of
        them to annul the trade within a defined period or under certain
        conditions, then that clause becomes legally binding and must be honored
        according to the terms agreed upon.
        <br />
        For instance, if a buyer decides to purchase a house but expresses
        uncertainty about its suitability, he may include a condition in the
        contract stating that he will live in the house for thirty days to test
        its comfort and functionality. If, during that period, he finds it
        unsatisfactory, he will have the right to cancel the trade, return the
        property, and reclaim his payment. The thirty-day duration, as per the
        stipulation, begins immediately after the transaction is concluded, and
        not after the buyer and seller part ways from the meeting where the sale
        occurred.
        <br />
        This right provides a balance between commitment and caution, ensuring
        that neither party suffers loss due to hidden defects, unmet
        expectations, or unforeseen circumstances. It also upholds the Qur’anic
        principle of mutual consent in trade: <br />
        "O believers! Do not devour one another’s wealth illegally, but rather
        trade by mutual consent. And do not kill ˹each other or˺ yourselves.
        Surely Allah is ever Merciful to you."{" "}
        <a href="https://quran.com/4/29" target="blank">
          (Surah An-Nisa 4:29)
        </a>
        .<br />
        By allowing conditional clauses that both parties freely agree to,
        Islamic law preserves justice and trust while discouraging exploitation
        or deception.
        <br />
        Khiyār ash-Shart exemplifies the moral spirit of Islamic transactions,
        where fairness and mutual satisfaction are prioritized over rigid
        legalism. It encourages transparency, deliberation, and accountability,
        reminding both buyer and seller that commerce in Islam is not merely a
        transfer of ownership but a social contract grounded in ethics and
        sincerity.
      </p>
      <p className="content">
        3){" "}
        <b>
          Right to annual a trade in case of a fraud <em>(Khiyār al-Ghabn)</em>:
        </b>{" "}
        <br />
        <br />
        Khiyār al-Ghabn refers to the right of annulment when a buyer or seller
        has been unfairly exploited in a transaction due to a significant price
        imbalance or fraudulent advantage taken by one party. Islamic law
        upholds justice in commerce and prohibits any form of taking advantage
        of another person’s lack of knowledge, urgency, or inexperience. Ghabn
        occurs when a product is sold for a price far above or far below its
        actual market value in a way that is considered excessively unfair and
        intentional. This form of fraud does not always require concealment of
        defects; rather, it involves manipulating the price to an unreasonable
        degree so that one party is harmed financially.
        <br />
        For example, if a merchant sells a product worth 10,000 sats to an
        unsuspecting or uninformed buyer for 3,000 sats, the seller has taken
        advantage of the buyer’s ignorance and created an imbalance that Islamic
        law views as unjust. Once the buyer discovers the true market value, he
        has the right to annul the transaction under Khiyār al-Ghabn. Similarly,
        if a seller is pressured or deceived into selling his goods for far less
        than their worth, he too may revoke the sale upon learning of the
        exploitation.
        <br />
        This principle safeguards fairness by ensuring that trade is not turned
        into a means of exploiting others’ vulnerabilities. Islam teaches that
        commerce should be grounded in transparency, mercy, and mutual benefit.
        The Prophet Muhammad (peace be upon him) encouraged honest pricing and
        warned against taking advantage of others in financial matters. By
        recognizing the right of annulment in cases of excessive unfairness,
        Khiyār al-Ghabn reinforces the moral foundation of trade, ensuring that
        wealth is earned through integrity rather than manipulation or
        opportunistic gain.
        <br />
        <br />
      </p>
      <p className="content">
        4){" "}
        <b>
          Right to annual a trade in case of a misrepresentation{" "}
          <em>(Khiyār at-Tadlīs)</em>:
        </b>{" "}
        <br />
        <br />
        Islamic law places a strong emphasis on truthfulness and fairness in
        trade, ensuring that no party is misled or deceived during a
        transaction. Khiyār at-Tadlīs refers to the right of annulment when a
        trade involves misrepresentation or concealment of facts intended to
        deceive the other party. This right protects the buyer or seller from
        harm caused by dishonesty in presenting the product’s condition,
        quality, or performance. Misrepresentation can occur in two primary
        ways: when a defective product is portrayed as defect-free, and when a
        defect-free product is exaggerated to appear better than it truly is.
        <br />
        In the first case, the deception occurs through concealment. For
        example, a fruit vendor may place fresh apples on the top of his cart
        for display while hiding rotten or damaged ones underneath. A buyer,
        assuming all the apples are of the same quality, purchases the fruit
        based on a false impression. Upon discovering the deception, the buyer
        has the right to annul the trade, return the goods, and reclaim his
        money. This ensures that honesty remains the basis of commercial
        dealings and that no one profits through trickery or concealment.
        <br />
        In the second case, the misrepresentation occurs through exaggeration or
        false claims. For instance, if a car manufacturer advertises that a
        particular vehicle offers higher mileage or better performance than it
        actually does, the buyer is being misled about the product’s true
        capabilities. Once the truth is revealed, the buyer has the right to
        revoke the sale under Khiyār at-Tadlīs.
        <br />
        This principle serves to preserve justice and sincerity in trade,
        discouraging marketing practices or sales tactics that distort reality.
        Islam views trade not merely as a financial exchange but as a moral act
        that must be guided by honesty, trust, and accountability. The Prophet
        Muhammad (peace be upon him) warned against deceit in trade, saying that
        both parties should be truthful and transparent, for only then will
        their transaction be blessed. Khiyār at-Tadlīs ensures that commercial
        relationships remain built on mutual respect and that no one gains at
        the expense of another’s ignorance or misplaced trust.
        <br />
      </p>
      <p className="content">
        <b>
          5) Right to annul a trade due to a defect <em>(Khiyār al-‘Ayb):</em>
        </b>
        <br />
        <br />
        Islamic commercial law upholds the principle that transactions must be
        based on transparency, mutual consent, and fairness. When one of these
        conditions is violated due to a hidden defect in the product being sold,
        the buyer is granted the right known as Khiyār al-‘Ayb. This right
        allows the buyer to annul the trade if it is discovered that the item
        purchased contains a defect that was not disclosed at the time of sale
        and that significantly reduces the item’s value or usefulness. The
        rationale behind this ruling is to protect the buyer from injustice and
        to ensure that the seller does not benefit from deceit or concealment.
        <br />
        The right of Khiyār al-‘Ayb serves to ensure trust and ethical dealing
        in the marketplace. It discourages sellers from concealing faults and
        encourages complete honesty in describing the condition of goods. The
        Prophet Muhammad (peace be upon him) said, <br />
        “Both parties in a business transaction have the right to annul it so
        long as they have not separated; and if they speak the truth and make
        everything clear they will be blessed in their transaction; but if they
        tell a lie and conceal anything the blessing on their transaction will
        be blotted out.”{" "}
        <a href="https://sunnah.com/muslim:1532a" target="blank">
          (Sahih Muslim 1532)
        </a>
        <br />
        This hadith captures the essence of why Khiyār al-‘Ayb exists: to
        maintain fairness, protect trust between traders, and keep commerce free
        from dishonesty and exploitation. Through this principle, Islam promotes
        a marketplace rooted in moral integrity, where every trade is conducted
        in good faith and with full disclosure.
        <br />
        If a dispute occures between the buyer and the seller regarding when did
        the defect occur, did it occur when the good being sold was under the
        posession of the seller or did it occur when it was under the posession
        of the buyer, this situation can have the following three scenarios:{" "}
        <br></br>i) If it is evident that the defect occured when the good was
        under the possesion of the seller then the buyer has a right to annul
        the contract and take his money back. <br />
        Eg: A goat was sold and immediately after it's sale the buyer claims
        that he has been sold a defected goat as it has an extra limb. It is
        evident that the limb cannot grow immediately and thus the defect
        occured when the goat was in the posession of the seller.
        <br />
        ii) If it is evident that the defect occured when the good was under the
        possesion of the buyer then the buyer has no right to annul the contract
        and take his money back. <br />
        Eg: A goat sold sold a week ago and it has an injury due to a fresh
        wound, it is evident that the wound is fresh and has been inflicted
        recently when the goat was under the posession of the buyer, in this
        case the buyer does not have the right to annul.
        <br />
        iii) If it is not evident whether the defect occured when the good was
        in the posession of the buyer or the seller, then the claimant of the
        defect will have to prove that the defect occured in the presence of the
        counter-party as the burden of proof is upon the claimant. If the
        claimant cannot prove that the defect existed before the good was
        transferred to his possesion then the counter-party will have to take an
        oath to free himself from the blame.
        <br /> Eg: A buyer claims that a goat he purchased had a defect before
        he purchased it but does not have a proof of this claim, the seller
        denies that the goat had any defect when he sold it, in this case the
        burden of proof is upon the buyer, if he can prove his claim then he has
        a right to annul the trade, and if he cannot, then the seller will have
        to take an oath stating that the product did not have any defect when he
        sold it to free himself of any blame.
      </p>
      <p className="content">
        <b>
          6) Right to annul a trade due to incorrect price disclosure{" "}
          <em>(Khiyār takhbīr bi thaman): </em>
        </b>{" "}
        <br />
        <br />
        In Islamic commercial jurisprudence, honesty in disclosing the cost or
        price of a product is crucial for maintaining fairness and trust in
        trade. Khiyār takhbīr bi thaman refers to the buyer’s right to annul a
        trade if the seller quotes an incorrect purchase price for an item that
        he himself procured or manufactured. This ruling exists to prevent the
        seller from misleading the buyer through deception regarding the origin
        or cost of the product, which could influence the buyer’s decision and
        lead to an unfair transaction.
        <br />
        For example, if a seller informs a buyer that he will sell a house at
        the same price at which he originally purchased it, let us say 1 BTC,
        but the buyer later discovers that the seller actually bought the house
        for 0.8 BTC, this constitutes incorrect price disclosure. In this
        situation, the buyer has the right to annul the sale because the
        seller’s statement about the purchase price was misleading and affected
        the buyer’s perception of the fairness of the transaction. The principle
        emphasizes that the buyer’s consent must be based on accurate
        information, and any intentional misrepresentation undermines that
        consent.
        <br />
        The wisdom behind this right is to ensure transparency and prevent
        exploitation in business dealings. It also discourages sellers from
        inflating the perceived value of their goods through false claims about
        prior costs, which could result in an unjust profit at the expense of
        the buyer. Islamic law places great importance on honesty,
        trustworthiness, and equity in commerce, and Khiyār takhbīr bi thaman
        serves as a safeguard to uphold these values.
        <br />
        This principle complements other rights of annulment in Islamic trade by
        reinforcing that transactions should not only be consensual but also
        informed. By granting the buyer the ability to annul a trade under such
        circumstances, Islam protects the integrity of the marketplace and
        ensures that all parties engage in transactions with transparency and
        good faith. It serves as a reminder that ethical conduct in trade is not
        optional but an essential element of lawful and blessed commerce.
      </p>
      <p className="content">
        <b>
          7) Right to annul a trade due to an irreconcilable dispute{" "}
          <em>(Khiyār li Ikhtilāf al mutabāyi'ain):</em>
        </b>{" "}
        <br />
        <br />
        In Islamic commercial law, the preservation of fairness and mutual
        satisfaction is paramount, even after a contract has been executed.
        Khiyār li Ikhtilāf al-Mutabāyi'ain grants both the buyer and the seller
        the right to annul a trade if an irreconcilable dispute arises over the
        agreed price. This right recognizes that honest disagreements can occur
        during transactions and provides a mechanism to prevent prolonged
        conflict, injustice, or exploitation that may result from unresolved
        disputes.
        <br />
        If the buyer and the seller dispute over the price that was agreed and
        the dispute is irreconcilable, then each one of them has a right to
        annul the trade. This situation can have multiple scenarios. <br />
        i) If the buyer and the seller have a dispute over the price agreed
        upon, then we look for an evidence that can establish the price at which
        the deal was agreed.
        <br />
        ii) If none of them have an evidence to establish their claim, then it
        is seen if the claim of any of them is infeasible. If the buyer is
        stating a price way below the market fair price of the good or the
        seller is stating a price way above the market fair price of the good
        then that could be used as a strong evidence that the claimant is lying.{" "}
        <br />
        iii) If none of the contracting parties have any evidence to prove their
        claim , nor are they making infeasible claims, then both of them are
        asked to take an oath to prove their claim is rightfull. If one of them
        refuses to take an oath then the decision will be made against him.
        <br />
        iv) If both of them take an oath and none is willing to back off, then
        they have a right to annul the trade.
        <br />
        <b>Case:</b> If the product is lost or destroyed by the buyer before
        paying the price, then he will have to compensate the seller as per the
        fair market price irresepective of what the contracting parties claim.
      </p>
      <p className="content">
        <b>
          8) Right to annul due to difference in attributes described{" "}
          <em>(Khiyār lil khulf fee as-sifah): </em>
        </b>
        <br />
        <br />
        Islamic commercial law emphasizes transparency, accuracy, and honesty in
        transactions, particularly when specific attributes or qualities of a
        product are agreed upon before the sale. Khiyār lil Khulf fi as-Sifah
        grants the buyer the right to annul a trade if the delivered product
        does not match the attributes or qualities described prior to the
        agreement. This right protects the buyer from being misled, even
        unintentionally, and ensures that the terms of the agreement are
        respected.
        <br />
        For example, if a seller describes a car to a buyer and specifies
        certain features such as model, mileage, or color, and the buyer agrees
        to purchase the car based on these attributes, the transaction is
        contingent on the product meeting the described specifications. If the
        car delivered differs from what was described, the buyer has the right
        to annul the trade and reclaim his payment. This rule applies regardless
        of whether the discrepancy was intentional or accidental because the
        buyer’s consent was based on a specific understanding of the product.
        <br />
        The principle behind this right is to preserve fairness and uphold the
        integrity of the transaction. By allowing annulment in cases where the
        product does not conform to the agreed description, Islam ensures that
        buyers are not disadvantaged due to false impressions or
        miscommunication. It also encourages sellers to provide accurate
        information and avoid exaggeration, thus promoting ethical business
        practices.
        <br />
        Khiyār lil Khulf fi as-Sifah complements other rights of annulment, such
        as those related to defects, misrepresentation, or fraud, by addressing
        situations where the disagreement arises from the product not meeting
        the buyer’s prior expectations as explicitly stated. This principle
        reinforces the moral foundation of commerce in Islam, reminding both
        buyers and sellers that honesty, clarity, and trustworthiness are
        essential for transactions to be valid, just, and blessed. It safeguards
        the buyer’s right to receive exactly what was promised, ensuring that
        trade is conducted with fairness and mutual respect.
      </p>
      <p className="content">
        <b>
          9) Right to annul a trade due to deterioration in the product{" "}
          <em>(Khiyār at-taghayyur):</em>
        </b>{" "}
        <br />
        <br />
        Islamic commercial law prioritizes fairness and ensures that the buyer
        receives exactly what was agreed upon at the time of sale. Khiyār
        at-Taghayyur grants the buyer the right to annul a trade if the
        condition of the good deteriorates before it is delivered or before the
        buyer takes possession. This rule protects buyers from losses caused by
        changes in the product’s quality or usability that occur after the
        contract is executed but before ownership is transferred.
        <br />
        For example, if a buyer purchases milk with an agreement for delivery
        the next day, and upon receiving he discovers that the milk has turned
        sour, he has the right to annul the trade. The deterioration of the milk
        renders it unfit for consumption and violates the expectation
        established during the sale. In such cases, the buyer may reject the
        product and reclaim his payment because the quality or condition of the
        good no longer matches what was agreed upon.
        <br />
        The purpose of Khiyār at-Taghayyur is to maintain justice and trust in
        commercial transactions. It prevents sellers from transferring goods
        that have lost value or usefulness through no fault of the buyer,
        ensuring that the buyer’s consent remains valid and informed. It also
        encourages sellers to handle and store goods responsibly until delivery,
        as the law holds them accountable for maintaining the product’s
        agreed-upon condition.
        <br />
        This right complements other annulment rights in Islamic law, such as
        those related to defects, misrepresentation, or fraud, by addressing
        situations where the product changes in quality after the sale. By
        granting the buyer the option to annul the trade, Islam reinforces
        ethical commerce, fairness, and the protection of both parties’ rights.
        It underscores that trade is not merely a transfer of ownership but a
        moral and social interaction that requires honesty, diligence, and
        accountability, ensuring that every transaction is just and blessed.
        <br />
        <br /> <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        On Day 4 of our journey into Islamic jurisprudence on Finance we focused
        on the concept of Khiyar, the right to annul a trade under specific
        conditions. Khiyar ensures fairness, transparency, and mutual
        satisfaction in sales contracts by allowing buyers or sellers to cancel
        a transaction when necessary. Different types of Khiyar include the
        right to annul during the sitting, annul based on stipulated conditions,
        annul due to defects, misrepresentation, fraud, incorrect price
        disclosure, disputes over price, differences in described attributes,
        and deterioration of the product before delivery. These rights are
        rooted in both the Quran and authentic Hadith, emphasizing honesty,
        mutual consent, and ethical conduct in trade. By providing mechanisms to
        address defects, deception, or unexpected changes, Khiyar protects the
        interests of all parties, promotes trust, and upholds the moral and
        ethical foundations of commercial transactions in Islam.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        Tomorrow, we will cover a foundational principle that ensures clarity of
        ownership and liability in trade:
        <br /> the Prohibition of Disposal before Possession, known in Arabic as
        Nāhī an at-Taṣarruf qabla al-Qabḍ. This rule prohibits a buyer from
        selling, gifting, or using an item before they have taken possession of
        it whether physically or constructively.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicTradePage4;
